import { Button } from "@heroui/react";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { NAV_BAR_OPTIONS } from "./nav";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import sentinelLogo from "@/assets/images/Sentinel-Logo-Icon.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { useTour } from "@/lib/tour/useTour";
import { TourGuide } from "@/lib/tour/TourGuide";
import { driverObj } from "@/lib/tour/driverConfig";

const TOUR_START_DELAY = 500;
const TOUR_ADVANCE_DELAY = 500;
const TOUR_RESET_DELAY = 300;

function MobileNavBar() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const router = useRouter();
    const navigate = useNavigate();
    const [pathname, setPathname] = useState(router.state.location.pathname);
    const { isTourActive, currentTourStep, startTour, hasSeenTour, resetTour } = useTour();
    const [isStartingTour, setIsStartingTour] = useState(false);
    const tourButtonTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        const unsubscribe = router.subscribe("onResolved", ({ toLocation }) => {
            setPathname(toLocation.pathname);
        });
        return unsubscribe;
    }, [router]);

    useEffect(() => {
        if (!hasSeenTour && (pathname === '/dashboard/home' || pathname === '/dashboard')) {
            setTimeout(() => {
                startTour();
            }, TOUR_START_DELAY);
        }
    }, [pathname, startTour, hasSeenTour]);

    const toggleMenu = () => {
        setIsMenuOpen((open) => !open);
    };

    useEffect(() => {
        const assignToggleFunction = () => {
            if (window._tourContext) {
                window._tourContext.toggleMobileMenu = toggleMenu;
            } else {
                setTimeout(assignToggleFunction, 100);
            }
        };

        assignToggleFunction();

        return () => {
            if (window._tourContext) {
                window._tourContext.toggleMobileMenu = undefined;
            }
        };
    }, []);

    const advanceTourIfNeeded = useCallback((expectedStep: number, targetAttr: string) => {
        if (isTourActive && currentTourStep === expectedStep && targetAttr) {
            setTimeout(() => {
                if (driverObj.isActive()) {
                    driverObj.moveNext();
                }
            }, TOUR_ADVANCE_DELAY);
        }
    }, [isTourActive, currentTourStep]);

    const handleTourButtonClick = useCallback(() => {
        if (isStartingTour) return;

        setIsStartingTour(true);

        if (isMenuOpen) {
            setIsMenuOpen(false);
        }

        if (driverObj.isActive()) {
            driverObj.destroy();
        }

        if (tourButtonTimeoutRef.current) {
            clearTimeout(tourButtonTimeoutRef.current);
        }

        resetTour();

        tourButtonTimeoutRef.current = window.setTimeout(() => {
            startTour();
            setIsStartingTour(false);
        }, TOUR_RESET_DELAY);

    }, [resetTour, startTour, isStartingTour, isMenuOpen]);

    useEffect(() => {
        return () => {
            if (tourButtonTimeoutRef.current) {
                clearTimeout(tourButtonTimeoutRef.current);
            }
        };
    }, []);

    const options = useMemo(() => {
        return NAV_BAR_OPTIONS?.map((opt) => {
            const color = pathname.includes(opt.route) ? "#74BD43" : "#09426A";

            const tourAttribute =
                opt.label === "Home" ? "home-option" :
                    opt.label === "Fields" ? "fields-option" :
                        opt.label === "Planner" ? "planner-option" :
                            opt.label === "Documentation" ? "documentation-option" :
                                opt.label === "Network" ? "network-option" :
                                    opt.label === "Enrollment" ? "enrollment-option" :
                                        opt.label === "Settings" ? "settings-option" :
                                            undefined;

            return (
                <Button
                    key={opt.route}
                    variant="light"
                    onPress={() => {
                        setIsMenuOpen(false);
                        navigate({ to: opt.route });

                        advanceTourIfNeeded(1, tourAttribute || "");
                    }}
                    className="w-full justify-start"
                    data-tour={tourAttribute}
                >
                    {opt.component({ color })}
                    <div className="font-bold text-2xl" style={{ color }}>
                        {opt.label}
                    </div>
                </Button>
            );
        });
    }, [navigate, pathname, advanceTourIfNeeded]);

    return (
        <>
            <TourGuide />
            <div className="bg-white h-14 flex flex-row gap-2 w-screen px-4 items-center z-10 border-b border-b-gray-300">
                <Button
                    isIconOnly
                    variant="light"
                    onPress={toggleMenu}
                    aria-label="Open Menu"
                    data-tour="hamburger-menu"
                >
                    <FontAwesomeIcon icon={faBars} />
                </Button>
                <Link
                    to="/dashboard"
                    className="flex flex-row gap-1 items-center"
                >
                    <img src={sentinelLogo} className="h-12 p-1" />
                    <p className="font-extrabold text-brandNavy-500 text-4xl font-heisman">
                        Sentinel
                    </p>
                </Link>
                <div className="ml-auto">
                    <Button
                        isIconOnly
                        variant="light"
                        onPress={handleTourButtonClick}
                        aria-label="Start product tour"
                        disabled={isStartingTour}
                    >
                        <FontAwesomeIcon
                            icon={faQuestionCircle}
                            size="lg"
                            className="text-primary-500"
                        />
                    </Button>
                </div>
            </div>
            <div
                className="absolute backdrop-blur-md bg-white/80 transition-size duration-200 left-0 top-14 w-full z-[1000]"
                style={{
                    height: isMenuOpen ? "calc(100% - 56px)" : "0",
                    overflow: isMenuOpen ? "auto" : "hidden",
                }}
                data-test-id="mobile-menu-container"
            >
                <div className="flex flex-col gap-1">{options}</div>
            </div>
        </>
    );
}
export default MobileNavBar;
